import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CreditCard,
  Package,
  Puzzle,
  CheckCircle2,
  ArrowRight,
  Users,
  HardDrive,
  DollarSign,
  MessageSquare,
  ExternalLink,
  Zap,
  Info,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import {
  subscriptionsTenantService,
  BillingSummary,
  PlanAddon,
} from '@/features/branding/services/subscriptions.service';

export const Route = createFileRoute('/_authenticated/admin/subscription')({
  component: SubscriptionAdminRoute,
});

function SubscriptionAdminRoute() {
  return (
    <PermissionGate permission={Permission.ADMIN_TENANT}>
      <SubscriptionPage />
    </PermissionGate>
  );
}

function SubscriptionPage() {
  const [requestAddon, setRequestAddon] = useState<PlanAddon | null>(null);
  const [requestPlanUpgrade, setRequestPlanUpgrade] = useState(false);

  const { data: billing, isLoading: billingLoading } = useQuery<BillingSummary>({
    queryKey: ['subscription', 'my-billing'],
    queryFn: () => subscriptionsTenantService.getMyBilling(),
  });

  const { data: allAddons = [], isLoading: addonsLoading } = useQuery<PlanAddon[]>({
    queryKey: ['subscription', 'addons-catalog'],
    queryFn: () => subscriptionsTenantService.listAddons(),
  });

  const activeAddonKeys = new Set(
    billing?.addons.filter((a) => a.status === 'active').map((a) => a.addon.featureKey) ?? [],
  );

  const availableAddons = allAddons.filter(
    (a) => a.isActive && !activeAddonKeys.has(a.featureKey),
  );
  const contractedAddons = allAddons.filter(
    (a) => activeAddonKeys.has(a.featureKey),
  );

  if (billingLoading || addonsLoading) {
    return (
      <div className="p-6 max-w-4xl space-y-4 animate-pulse">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="h-40 bg-muted rounded-xl" />
        <div className="h-60 bg-muted rounded-xl" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">
      {/* Cabeçalho */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <CreditCard className="h-6 w-6" />
          Minha Assinatura
        </h1>
        <p className="text-muted-foreground mt-1">
          Visualize seu plano atual, módulos contratados e solicite alterações.
        </p>
      </div>

      {/* Plano atual */}
      {billing?.plan ? (
        <PlanSummaryCard
          billing={billing}
          onRequestUpgrade={() => setRequestPlanUpgrade(true)}
        />
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-10 text-center space-y-3">
            <Package className="h-10 w-10 text-muted-foreground mx-auto" />
            <p className="font-medium">Nenhuma assinatura ativa</p>
            <p className="text-sm text-muted-foreground">
              Entre em contato para contratar um plano.
            </p>
            <Button onClick={() => setRequestPlanUpgrade(true)} className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Solicitar plano
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add-ons ativos */}
      {contractedAddons.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            Módulos contratados
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {contractedAddons.map((addon) => (
              <AddonCard key={addon.id} addon={addon} active />
            ))}
          </div>
        </section>
      )}

      {/* Add-ons disponíveis */}
      {availableAddons.length > 0 && (
        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Puzzle className="h-5 w-5 text-indigo-500" />
              Módulos disponíveis
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Expanda sua plataforma contratando módulos adicionais.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {availableAddons.map((addon) => (
              <AddonCard
                key={addon.id}
                addon={addon}
                active={false}
                onRequest={() => setRequestAddon(addon)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Aviso sem gateway */}
      <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/40 p-4 text-sm text-blue-800 dark:text-blue-300">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <p>
          Alterações de plano e contratação de módulos são processadas pela nossa equipe.
          Ao solicitar, entraremos em contato em até 24h para confirmar e ativar o acesso.
        </p>
      </div>

      {/* Dialogs */}
      {requestAddon && (
        <RequestAddonDialog
          addon={requestAddon}
          onClose={() => setRequestAddon(null)}
        />
      )}

      {requestPlanUpgrade && (
        <RequestPlanDialog onClose={() => setRequestPlanUpgrade(false)} />
      )}
    </div>
  );
}

// ─── Card do plano atual ──────────────────────────────────────────────────────

function PlanSummaryCard({
  billing,
  onRequestUpgrade,
}: {
  billing: BillingSummary;
  onRequestUpgrade: () => void;
}) {
  return (
    <Card className="border-primary/20 bg-gradient-to-br from-background to-primary/5">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              {billing.plan!.label}
            </CardTitle>
            <CardDescription>
              Ciclo {billing.billingCycle === 'annual' ? 'anual' : 'mensal'}
            </CardDescription>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold">
              R$ {billing.breakdown.total.toFixed(2).replace('.', ',')}
            </p>
            <p className="text-xs text-muted-foreground">/mês</p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Breakdown */}
        <div className="grid grid-cols-3 gap-3">
          <BreakdownItem
            icon={<DollarSign className="h-4 w-4 text-primary" />}
            label="Plano base"
            value={`R$ ${billing.breakdown.base.toFixed(0)}`}
          />
          <BreakdownItem
            icon={<Users className="h-4 w-4 text-primary" />}
            label={`${billing.userSlots} usuários`}
            sublabel={billing.extraUsers > 0 ? `+${billing.extraUsers} extra` : undefined}
            value={`R$ ${billing.breakdown.users.toFixed(0)}`}
          />
          <BreakdownItem
            icon={<Puzzle className="h-4 w-4 text-primary" />}
            label={`${billing.addons.filter((a) => a.status === 'active').length} add-ons`}
            value={`R$ ${billing.breakdown.addons.toFixed(0)}`}
          />
        </div>

        <Separator />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <HardDrive className="h-3.5 w-3.5" />
              {billing.storageGb} GB armazenamento
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={onRequestUpgrade} className="gap-1.5">
            <Zap className="h-3.5 w-3.5" />
            Solicitar alteração
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function BreakdownItem({
  icon,
  label,
  sublabel,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  value: string;
}) {
  return (
    <div className="flex flex-col items-center text-center p-3 rounded-lg bg-background/60 border">
      <div className="mb-1">{icon}</div>
      <p className="text-sm font-semibold">{value}</p>
      <p className="text-xs text-muted-foreground leading-tight">{label}</p>
      {sublabel && <p className="text-xs text-muted-foreground">{sublabel}</p>}
    </div>
  );
}

// ─── Card de addon ────────────────────────────────────────────────────────────

function AddonCard({
  addon,
  active,
  onRequest,
}: {
  addon: PlanAddon;
  active: boolean;
  onRequest?: () => void;
}) {
  return (
    <div
      className={`rounded-xl border p-4 flex flex-col gap-3 transition-colors ${
        active
          ? 'border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20'
          : 'border-border hover:border-primary/40'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm leading-tight">{addon.name}</p>
          {addon.description && (
            <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
              {addon.description}
            </p>
          )}
        </div>
        {active ? (
          <Badge className="bg-green-600 text-white shrink-0">Ativo</Badge>
        ) : null}
      </div>

      <div className="flex items-center justify-between mt-auto pt-1">
        <div>
          <span className="text-lg font-bold">
            R$ {Number(addon.priceBrl).toFixed(0)}
          </span>
          <span className="text-xs text-muted-foreground">/mês</span>
        </div>
        {!active && onRequest && (
          <Button size="sm" onClick={onRequest} className="gap-1.5">
            Contratar
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Dialog: solicitar addon ──────────────────────────────────────────────────

// Número vem de VITE_SUPPORT_WHATSAPP (só dígitos, com DDI); sem ele o botão some.
export function supportWhatsappUrl(raw: string | undefined = import.meta.env.VITE_SUPPORT_WHATSAPP) {
  const digits = (raw ?? '').replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : null;
}

const copyMessage = (msg: string) =>
  navigator.clipboard
    .writeText(msg)
    .then(() => toast.success('Mensagem copiada!'))
    .catch(() => toast.error('Não foi possível copiar. Copie o texto manualmente.'));

function RequestAddonDialog({
  addon,
  onClose,
}: {
  addon: PlanAddon;
  onClose: () => void;
}) {
  const handleCopyMessage = () => {
    const msg = `Olá! Gostaria de contratar o módulo "${addon.name}" (${addon.featureKey}) por R$${Number(addon.priceBrl).toFixed(0)}/mês para a minha conta.`;
    copyMessage(msg);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Puzzle className="h-5 w-5 text-indigo-500" />
            Contratar: {addon.name}
          </DialogTitle>
          <DialogDescription>
            Envie uma solicitação para nossa equipe ativar este módulo na sua conta.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Resumo do addon */}
          <div className="rounded-lg border bg-muted/40 p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Módulo</span>
              <span className="font-medium">{addon.name}</span>
            </div>
            {addon.description && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Descrição</span>
                <span className="text-right max-w-48">{addon.description}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between text-base font-semibold">
              <span>Valor mensal</span>
              <span>R$ {Number(addon.priceBrl).toFixed(2).replace('.', ',')}</span>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            Clique em "Copiar mensagem" e envie ao suporte.
            Nossa equipe ativará o módulo em até 24h após a confirmação.
          </p>
        </div>

        <DialogFooter className="flex flex-wrap gap-2 justify-between">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleCopyMessage} className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Copiar mensagem
            </Button>
            {supportWhatsappUrl() && (
              <Button
                className="gap-2"
                onClick={() => window.open(supportWhatsappUrl()!, '_blank')}
              >
                <ExternalLink className="h-4 w-4" />
                Falar no WhatsApp
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Dialog: solicitar mudança de plano ───────────────────────────────────────

export function RequestPlanDialog({ onClose }: { onClose: () => void }) {
  const handleCopyMessage = () => {
    const msg = `Olá! Gostaria de solicitar uma alteração no meu plano / quantidade de usuários.`;
    copyMessage(msg);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Solicitar alteração de plano
          </DialogTitle>
          <DialogDescription>
            Para ajustar seu plano ou quantidade de usuários, fale com nossa equipe.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            Nossa equipe vai verificar sua necessidade e aplicar a alteração com o ajuste
            proporcional no próximo ciclo de cobrança.
          </p>

          <div className="rounded-lg border bg-muted/40 p-4 text-sm space-y-1.5">
            <p className="font-medium mb-2">Você pode solicitar:</p>
            {[
              'Aumentar ou reduzir quantidade de usuários',
              'Fazer upgrade para um plano superior',
              'Alterar ciclo de cobrança (mensal → anual)',
              'Contratar armazenamento adicional',
            ].map((item) => (
              <div key={item} className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="flex flex-wrap gap-2 justify-between">
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleCopyMessage} className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Copiar mensagem
            </Button>
            {supportWhatsappUrl() && (
              <Button
                className="gap-2"
                onClick={() => window.open(supportWhatsappUrl()!, '_blank')}
              >
                <ExternalLink className="h-4 w-4" />
                Falar no WhatsApp
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
